import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Sun, Cloud, CloudRain, CloudSnow, CloudLightning, Wind, 
  Droplets, ChevronRight, Loader2
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WEATHER_ICONS = {
  Clear: Sun,
  Clouds: Cloud,
  Rain: CloudRain,
  Drizzle: CloudRain,
  Thunderstorm: CloudLightning,
  Snow: CloudSnow,
  Mist: Cloud,
  Fog: Cloud,
  Haze: Cloud
};

export default function WeatherWidget({ destination, onExpand, showForecast = true }) {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchWeather = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API_URL}/api/weather/city/${encodeURIComponent(destination)}`);
      setWeather(response.data);
    } catch (err) {
      setError('Weather unavailable');
    } finally {
      setLoading(false);
    }
  }, [destination]);

  useEffect(() => {
    if (destination) {
      fetchWeather();
    }
  }, [destination, fetchWeather]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-[var(--bg-card)]/50 backdrop-blur-sm border border-[var(--border-primary)]">
        <Loader2 className="w-4 h-4 animate-spin text-[var(--color-primary)]" />
        <span className="text-sm text-[var(--text-secondary)]">Loading weather...</span>
      </div>
    );
  }

  if (error || !weather) return null;

  const current = weather.current;
  const WeatherIcon = WEATHER_ICONS[current?.condition?.main] || Sun;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="rounded-xl bg-gradient-to-r from-[var(--bg-card)] to-[var(--bg-secondary)] border border-[var(--border-primary)] overflow-hidden"
      data-testid="weather-widget"
    >
      {/* Current Weather */}
      <div className="p-4 flex items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-full bg-[var(--color-gold)]/10">
            <WeatherIcon className="w-8 h-8 text-[var(--color-gold)]" />
          </div>
          <div>
            <p className="text-3xl font-bold text-[var(--text-primary)]">
              {current?.temperature?.current}
              <span className="text-lg">{current?.temperature?.unit}</span>
            </p>
            <p className="text-sm text-[var(--text-secondary)]">{current?.condition?.description}</p>
          </div>
        </div>
        
        <div className="ml-auto flex items-center gap-4">
          <div className="hidden sm:flex flex-col items-center">
            <Droplets className="w-4 h-4 text-blue-400" />
            <span className="text-xs text-[var(--text-secondary)]">{current?.humidity}%</span>
          </div>
          <div className="hidden sm:flex flex-col items-center">
            <Wind className="w-4 h-4 text-cyan-400" />
            <span className="text-xs text-[var(--text-secondary)]">{current?.wind?.speed}</span>
          </div>
          
          {onExpand && (
            <button 
              onClick={onExpand}
              className="p-2 rounded-full hover:bg-[var(--bg-secondary)] transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-[var(--text-secondary)]" />
            </button>
          )}
        </div>
      </div>
      
      {/* Mini Forecast */}
      {showForecast && weather.forecast?.length > 0 && (
        <div className="px-4 pb-4">
          <div className="flex gap-2 overflow-x-auto scrollbar-hide">
            {weather.forecast.slice(0, 5).map((day, index) => {
              const DayIcon = WEATHER_ICONS[day.condition?.main] || Sun;
              return (
                <div
                  key={day.date}
                  className="flex-shrink-0 text-center px-3 py-2 rounded-lg bg-[var(--bg-secondary)]/50"
                >
                  <p className="text-xs text-[var(--text-secondary)]">
                    {index === 0 ? 'Today' : day.day_name?.slice(0, 3)}
                  </p>
                  <DayIcon className="w-5 h-5 mx-auto my-1 text-[var(--color-gold)]" />
                  <p className="text-sm font-medium text-[var(--text-primary)]">
                    {day.temperature?.max}°
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </motion.div>
  );
}
